/**
 * Shared setup for run, exec, and headless commands
 *
 * Extracts the duplicated env-var construction,
 * and sandbox-client creation shared by run.ts, exec.ts, and headless.ts.
 */

import type { SandboxClient } from '@spacecode/sandbox-client';
import type { ProxyMode } from '@spacecode/shared/remote';
import { SANDBOX_WORKER_URL } from '@spacecode/shared/remote';
import { resolveAuthToken, sandboxClientOptions } from '../config.js';
import { getGitHubToken } from '../sync/config.js';
import { buildEnvVars } from './env.js';

export interface RunSetupResult {
  client: SandboxClient;
  envVars: Record<string, string>;
}

/**
 * Gather config files, build env vars, and create a sandbox client.
 *
 * Shared by `run`, `exec`, and `headless` commands so the setup logic
 * lives in exactly one place.
 */
export async function setupRun(options?: { proxyMode?: ProxyMode }): Promise<RunSetupResult> {
  // Build env vars
  const ghToken = await getGitHubToken();
  const envVars = buildEnvVars({ ghToken, proxyMode: options?.proxyMode });

  // Create sandbox client
  const token = await resolveAuthToken();
  const { createSandboxClient } = await import('@spacecode/sandbox-client');
  const client = createSandboxClient(SANDBOX_WORKER_URL, token, sandboxClientOptions(token));

  return { client, envVars };
}
